<template>
  <div class="assigned-students-page" :class="{ 'assigned-students-page--mobile': $vuetify.breakpoint.mobile }">
    <v-progress-linear v-if="loading" indeterminate color="primary" />
    <div v-else class="students-content" :style="contentHeightStyle">
      <!-- Header strip -->
      <div class="students-header-bar primary" />
      <v-card class="students-card rounded-t-0 mb-0" flat>
        <v-card-text class="students-filters">
          <!-- Desktop: pills + search. Mobile: search + filter chip -->
          <div v-if="!$vuetify.breakpoint.mobile" class="filter-row mb-3">
            <v-btn v-for="cat in filterCategories" :key="cat.value" :color="filterItem === cat.value ? 'primary' : ''"
              :class="{ 'white--text': filterItem === cat.value }" small rounded depressed class="filter-pill mr-2 mb-2"
              @click="filterItem = cat.value">
              {{ cat.text }}
            </v-btn>
          </div>
          <div v-else class="mobile-search-row">
            <v-text-field v-model="filterText" :placeholder="searchLabel" prepend-inner-icon="mdi-magnify" filled dense
              clearable hide-details rounded background-color="grey lighten-4" class="mobile-search-field" />
            <v-menu offset-y left nudge-left="8">
              <template v-slot:activator="{ on, attrs }">
                <v-btn v-bind="attrs" v-on="on" small outlined rounded class="filter-menu-btn">
                  {{(filterCategories.find(c => c.value === filterItem) || filterCategories[0]).text}}
                  <v-icon right small>mdi-chevron-down</v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item v-for="cat in filterCategories" :key="cat.value" @click="filterItem = cat.value">
                  <v-list-item-title>{{ cat.text }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
          <v-text-field v-if="!$vuetify.breakpoint.mobile" v-model="filterText" :label="searchLabel"
            prepend-inner-icon="mdi-magnify" outlined dense clearable hide-details class="search-field" />
        </v-card-text>
      </v-card>

      <!-- Student list -->
      <div class="students-list" :class="{ 'students-list-mobile': $vuetify.breakpoint.mobile }">
        <template v-if="filteredStudents.length">
          <v-card v-for="student in filteredStudents" :key="student.id" class="student-card" elevation="0" outlined
            @click="$vuetify.breakpoint.mobile && goToStudent(student.id)">
            <v-card-text class="student-card-content">
              <!-- Mobile: compact single-row card -->
              <template v-if="$vuetify.breakpoint.mobile">
                <div class="mobile-card-row">
                  <v-avatar size="44" class="mr-3 flex-shrink-0">
                    <img v-if="student.profilePicture" :src="student.profilePicture" :alt="fullName(student)">
                    <v-icon v-else size="24" color="primary">mdi-account-circle</v-icon>
                  </v-avatar>
                  <div class="mobile-card-body min-width-0 flex-grow-1">
                    <div class="student-name font-weight-bold">{{ fullName(student) }}</div>
                    <div v-if="student.customEmail" class="student-email-mobile text-caption grey--text text--darken-1">
                      {{ student.customEmail }}
                    </div>
                    <div v-if="student.assignedAt" class="student-assigned-mobile text-caption grey--text">
                      {{ formatAssignedDate(student.assignedAt) }}
                    </div>
                  </div>
                  <v-icon class="flex-shrink-0 grey--text">mdi-chevron-right</v-icon>
                </div>
                <div class="mobile-card-actions d-flex align-center flex-wrap justify-space-between" @click.stop>
                  <v-btn color="primary" small class="view-profile-btn" @click.stop="addDirectBooking(student)">
                    <v-icon left small>mdi-plus</v-icon>
                    ADD BOOKING
                  </v-btn>
                </div>
              </template>
              <!-- Desktop: full card with date block and button -->
              <template v-else>
                <div class="d-flex align-center flex-wrap student-card-inner">
                  <div class="student-main flex-grow-1 min-width-0">
                    <div class="d-flex align-center mb-2 student-info-row">
                      <v-avatar size="64" class="mr-3 flex-shrink-0">
                        <img v-if="student.profilePicture" :src="student.profilePicture" :alt="fullName(student)">
                        <v-icon v-else size="32" color="primary">mdi-account-circle</v-icon>
                      </v-avatar>
                      <div class="min-width-0 flex-grow-1 student-details">
                        <div class="student-name font-weight-bold text-body-1">{{ fullName(student) }}</div>
                        <div v-if="student.customEmail" class="student-email text-caption grey--text text--darken-1">
                          <v-icon x-small left class="flex-shrink-0">mdi-email-outline</v-icon>
                          <span class="student-email-text">{{ student.customEmail }}</span>
                        </div>
                        <div v-if="student.assignedAt" class="student-assigned text-caption grey--text mt-1">
                          Assigned {{ formatAssignedDate(student.assignedAt) }}
                        </div>
                      </div>
                    </div>
                    <v-btn color="primary" small class="mt-2 view-profile-btn" @click.stop="goToStudent(student.id)">
                      VIEW PROFILE
                    </v-btn>
                    <v-btn color="primary" small class="mt-2 view-profile-btn ml-2"
                      @click.stop="addDirectBooking(student)">
                      <v-icon left small>mdi-plus</v-icon>
                      ADD BOOKING
                    </v-btn>
                  </div>
                  <div class="student-date-block primary text-center rounded mr-2">
                    <div class="text-caption white--text">Bookings</div>
                    <div class="font-weight-bold white--text">{{ getBookingRatio(student.id) }}</div>
                    <div class="text-caption white--text">used / total</div>
                  </div>
                  <div class="student-date-block primary text-center rounded">
                    <div class="date-month text-caption white--text">{{ assignedDateMonth(student.assignedAt) }}</div>
                    <div class="date-day font-weight-bold white--text">{{ assignedDateDay(student.assignedAt) }}</div>
                    <div class="date-year text-caption white--text">{{ assignedDateYear(student.assignedAt) }}</div>
                  </div>
                </div>
              </template>
            </v-card-text>
          </v-card>
        </template>
        <v-card v-else class="empty-state" flat>
          <v-card-text class="text-center py-10 py-sm-12">
            <v-icon size="64" color="grey lighten-1" class="mb-3 mb-sm-4">
              mdi-account-group-outline
            </v-icon>
            <div class="text-subtitle-1 text-sm-h6 grey--text">
              {{ emptyMessage }}
            </div>
          </v-card-text>
        </v-card>
      </div>
      <BookingModal v-model="showDirectBookingModal" :key="directBookingModalKey" :appointment="selectedStudent"
        :remaining-hours="999" :payment-method-added="true" mode="book" @submit="handleDirectBookingSubmit"
        @error="(msg) => $toast.error(msg)" />
    </div>
  </div>
</template>

<script>
import Parse from 'parse'
import { mapGetters, mapActions } from 'vuex'
import { isIphone } from '@/utils'
import BookingModal from '@/components/dashboard/BookingModal.vue'

export default {
  name: 'AssignedStudents',
  components: {
    BookingModal
  },
  data() {
    return {
      loading: true,
      filterItem: 'name',
      filterText: '',
      bookingSummaryByStudent: {},
      filterCategories: [
        { text: 'NAME', value: 'name' },
        { text: 'EMAIL', value: 'email' },
        { text: 'ASSIGNED DATE', value: 'date' }
      ],
      showDirectBookingModal: false,
      directBookingModalKey: 0,
      selectedStudent: null
    }
  },
  computed: {
    ...mapGetters(['user', 'assignedStudents']),
    searchLabel() {
      const labels = { name: 'Search by name', email: 'Search by email', date: 'Filter by date' }
      return labels[this.filterItem] || 'Search'
    },
    filteredStudents() {
      const list = this.assignedStudents || []
      const q = (this.filterText || '').toLowerCase().trim()
      if (!q) return list
      if (this.filterItem === 'name') {
        return list.filter(
          s =>
            (s.firstName || '').toLowerCase().includes(q) ||
            (s.lastName || '').toLowerCase().includes(q)
        )
      }
      if (this.filterItem === 'email') {
        return list.filter(s => (s.customEmail || '').toLowerCase().includes(q))
      }
      if (this.filterItem === 'date') {
        return list.filter(s => {
          const d = this.$moment(s.assignedAt).format('YYYY-MM-DD')
          return d.includes(q) || this.$moment(s.assignedAt).format('MMM D, YYYY').toLowerCase().includes(q)
        })
      }
      return list
    },
    emptyMessage() {
      if ((this.assignedStudents || []).length === 0) {
        return 'No students assigned to you yet.'
      }
      return 'No students match your search.'
    },
    contentHeightStyle() {
      if (this.$vuetify.breakpoint.mobile) {
        const top = 88
        const bottomNav = 56
        const iphoneExtra = isIphone && isIphone() ? 34 : 0
        return {
          position: 'relative',
          height: `calc(100vh - ${top + bottomNav + iphoneExtra}px)`,
          overflowY: 'auto',
          overflowX: 'hidden',
          webkitOverflowScrolling: 'touch'
        }
      }
      return {
        position: 'relative',
        height: `calc(100vh - 92px)`,
        overflowY: 'auto',
        overflowX: 'hidden'
      }
    }
  },
  async created() {
    await this.loadStudents()
  },
  methods: {
    ...mapActions(['getAssignedStudents']),
    addDirectBooking(student) {
      this.selectedStudent = student
      this.directBookingModalKey++
      this.showDirectBookingModal = true
    },
    async handleDirectBookingSubmit(formData) {
      console.log('handleDirectBookingSubmit called with formData:', formData)
      try {
        if (!this.selectedStudent?.id) {
          this.$toast.error('Student not found.')
          return
        }

        const instructor = Parse.User.current()
        if (!instructor?.id) {
          this.$toast.error('Instructor not found.')
          return
        }

        const lessonHours = Number(formData.lessonHours || 1)
        const [year, month, day] = formData.date.split('-').map(Number)
        const [startHour, startMinute] = (formData.startTime || '09:00').split(':').map(Number)

        const start = new Date(year, month - 1, day, startHour, startMinute)
        const end = new Date(start.getTime() + lessonHours * 60 * 60 * 1000)


        const Appointments = Parse.Object.extend('Appointments')
        const newBooking = new Appointments()

        const studentPointer = new Parse.Object('_User')
        studentPointer.id = this.selectedStudent.id

        const instructorPointer = new Parse.Object('_User')
        instructorPointer.id = instructor.id

        newBooking.set('studentId', studentPointer)
        newBooking.set('instructorId', instructorPointer)
        newBooking.set('start', start)
        newBooking.set('end', end)
        newBooking.set('status', 'Approved') // or 'Pending' if you want same as Card
        newBooking.set('type', ['booking_lesson'])
        newBooking.set('hoursLesson', String(lessonHours))
        newBooking.set('notes', formData.notes || '')
        newBooking.set('paymentMethod', 'online')
        newBooking.set('paid', false)
        newBooking.set('drivingLessonDates', [])

        await newBooking.save()

        // sending message to student
        const instructorName =
          `${instructor.get('firstName') || ''} ${instructor.get('lastName') || ''}`.trim() || 'Your instructor'

        const displayDate = this.$moment
          ? this.$moment(start).format('LLL')
          : start.toLocaleString()

        // Push to student
        const studentDeviceId = this.selectedStudent?.deviceId || this.selectedStudent?.attributes?.deviceId
        if (studentDeviceId) {
          await this.sendPushNotification({
            player_id: studentDeviceId,
            message: `Your instructor assigned you a driving lesson on ${displayDate}. Open DriverX for details.`,
            type: 'noti',
            senderId: instructor.id,
            name: instructorName
          })
        }

        // SMS to student
        const studentPhone = this.selectedStudent?.phone || this.selectedStudent?.attributes?.phone
        if (studentPhone) {
          await Parse.Cloud.run('sendAppointmentSMS', {
            to: studentPhone,
            message: `${instructorName} has assigned you a driving lesson on ${displayDate}. Please log in to the DriverX app for details.`
          })
        }

        // Email to student
        const studentEmail = this.selectedStudent?.customEmail || this.selectedStudent?.attributes?.customEmail
        if (studentEmail) {
          Parse.Cloud.run('approvedEmail', {
            name: this.fullName(this.selectedStudent),
            instructorName,
            date: start,
            email: studentEmail
          }).catch((e) => console.warn('approvedEmail:', e))
        }

        this.$toast.success('Booking added successfully.')
        this.showDirectBookingModal = false
        this.selectedStudent = null
      } catch (err) {
        console.error('handleDirectBookingSubmit error:', err)
        this.$toast.error(err?.message || 'Failed to add booking.')
      }
    },
    fullName(student) {
      return [student.firstName, student.lastName].filter(Boolean).join(' ') || 'Unknown'
    },
    formatAssignedDate(date) {
      if (!date) return ''
      return this.$moment(date).format('MMM D, YYYY')
    },
    assignedDateMonth(date) {
      if (!date) return '—'
      return this.$moment(date).format('MMM').toUpperCase()
    },
    assignedDateDay(date) {
      if (!date) return '—'
      return this.$moment(date).format('DD')
    },
    assignedDateYear(date) {
      if (!date) return '—'
      return this.$moment(date).format('YYYY')
    },
    async loadStudents() {
      this.loading = true
      const instructorId = this.user?.id
      if (instructorId) {
        const students = await this.getAssignedStudents({ instructorId })
        await this.loadBookingSummaries(students || [])
      }
      this.loading = false
    },
    goToStudent(id) {
      this.$router.push({ name: 'StudentProfile', params: { id } })
    },
    getBookingRatio(studentId) {
      const s = this.bookingSummaryByStudent[studentId]
      if (!s) return '0/0'
      return `${s.used}/${s.total || 0}`
    },
    async loadBookingSummaries(students) {
      const summary = {}
      await Promise.all(
        (students || []).map(async (student) => {
          try {
            const studentUser = Parse.User.createWithoutData(student.id)

            const userObj = await new Parse.Query('_User').get(student.id)
            const packageField = userObj.get('packageId')
            const packageId =
              typeof packageField === 'string'
                ? packageField
                : packageField?.id || ''

            let total = 0
            if (packageId) {
              const packageData = await new Parse.Query('SchoolPackages').get(packageId)
              total = parseInt(packageData?.get('freeBookings')) || 0
            }

            const appointments = await new Parse.Query('Appointments')
              .equalTo('studentId', studentUser)
              .containedIn('status', ['Finished', 'Approved'])
              .limit(1000)
              .find()

            let used = 0
            appointments.forEach((app) => {
              used += parseFloat(app.get('hoursLesson')) || 0
            })

            summary[student.id] = { used, total }
          } catch (e) {
            summary[student.id] = { used: 0, total: 0 }
          }
        })
      )
      this.bookingSummaryByStudent = summary
    }
  }
}
</script>

<style scoped>
.assigned-students-page {
  position: relative;
  min-height: 100%;
}

.students-header-bar {
  height: 6px;
  width: 100%;
  flex-shrink: 0;
}

.students-card {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  flex-shrink: 0;
}

.students-filters {
  padding: 16px;
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
}

.filter-row .v-btn {
  text-transform: none;
}

/* Mobile: search + filter dropdown in one row */
.mobile-search-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.mobile-search-field {
  flex: 1;
  min-width: 0;
}

.mobile-search-field>>>.v-input__slot {
  padding-left: 12px !important;
}

.filter-menu-btn {
  flex-shrink: 0;
  text-transform: none;
}

.students-list {
  padding: 16px;
  background: #fafafa;
}

.students-list-mobile {
  padding: 12px 16px 80px !important;
  background: #f5f7f5;
}

.student-card {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  transition: box-shadow 0.2s ease;
  overflow: hidden;
  margin-bottom: 10px;
}

.student-card:last-child {
  margin-bottom: 0;
}

.student-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.assigned-students-page--mobile .student-card {
  cursor: pointer;
}

.assigned-students-page--mobile .student-card:active {
  opacity: 0.96;
}

.student-card-content {
  padding: 16px !important;
}

.student-date-block {
  min-width: 56px;
  padding: 10px 12px;
  border-radius: 8px;
}

.student-name {
  line-height: 1.3;
  word-break: break-word;
}

.student-details {
  overflow: hidden;
}

.student-email {
  display: flex;
  align-items: center;
  min-width: 0;
  overflow: hidden;
}

.student-email-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.view-profile-btn {
  min-height: 40px;
}

.empty-state {
  border: 1px dashed rgba(0, 0, 0, 0.12);
  border-radius: 12px;
}

/* Mobile card: compact row */
.mobile-card-row {
  display: flex;
  align-items: center;
}

.mobile-card-actions {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  gap: 8px;
}

.mobile-add-booking-btn {
  flex: 1 1 auto;
  min-width: 0;
}

.mobile-card-body .student-name {
  font-size: 0.9375rem;
  line-height: 1.25;
}

.student-email-mobile,
.student-assigned-mobile {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-top: 2px;
}

.assigned-students-page--mobile .student-card-content {
  padding: 14px 16px !important;
}

/* Hide scrollbar on mobile for cleaner look */
.assigned-students-page--mobile .students-content {
  display: flex;
  flex-direction: column;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.assigned-students-page--mobile .students-content::-webkit-scrollbar {
  display: none;
}

@media (max-width: 599px) {
  .students-filters {
    padding: 12px 16px;
  }
}
</style>
